import { type MouseEvent, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router";

import {
  Button,
  Icon,
  Table,
  type TableColumn,
  Text,
  Tooltip,
  toastError,
} from "@alpacahq/ui/old";

import { useGetIsLegalEntity, useGetTradingAccount } from "~/v2/api/hooks";
import useGetOwners from "~/v2/api/hooks/useGetOwners";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Owner } from "~/v2/api/rest/account";
import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import { useCountry } from "../../../../../api/hooks/useCountry";
import type { EntityOnboardingHistoryState } from "../../constants";
import { usePageTimer } from "../../usePageTimer";
import { logAmplitudeEvent, type Transform } from "../../util";
import {
  AuthorizedIndividualsUBOsForm,
  DISPLAY_TYPE,
  type FormOwnerType,
} from "./AuthorizedIndividualsUBOsForm";
import { AUTHORIZED_INDIVIDUAL_UBOS_FAQ } from "./faq";
import { UBODropdownButton } from "./UBODropdownButton";
import { UBOQuickAddForm } from "./UBOQuickAddForm";

const getTotalPercentageOwnership = (owners: Owner[]) =>
  owners.reduce(
    (accumulator, { percentage_ownership }) =>
      accumulator + Number(percentage_ownership ?? 0),
    0,
  );

const getAvailablePercentageOwnership = (owners: Owner[]) =>
  100 - getTotalPercentageOwnership(owners);

export const AUTH_INDIVIDUAL_UBO_TRANSFORMS: [
  Transform<Owner, unknown, boolean>,
  Transform<Owner, string[], string>,
  Transform<Owner, number | string, number>,
  Transform<Owner, unknown, string>,
] = [
  {
    keys: [
      "is_affiliated_exchange_or_finra",
      "is_control_person",
      "is_politically_exposed",
      "immediate_family_exposed",
    ],
    condition: (value) => value == null,
    transform: () => false,
  },
  {
    keys: ["street_address", "controlling_firms"],
    condition: (value) => Array.isArray(value),
    transform: (value) => value.join(", "),
  },
  {
    keys: ["percentage_ownership"],
    condition: (value) =>
      value !== null &&
      !Number.isNaN(value as number) &&
      Number(value as number) >= 0,
    transform: (value) => Number(value),
  },
  {
    keys: "ALL_FIELDS",
    condition: (value) => value == null,
    transform: (_emptyValue) => "",
  },
];

export default function AuthIndividualsUBOsPage() {
  const history = useHistory<EntityOnboardingHistoryState>();
  const country = useCountry();

  const [state, setState] = useState<{
    // full form
    isFullFormModalOpen: boolean;
    fullFormOwnerType: FormOwnerType;
    // UBO quick add form
    isUBOQuickAddModalOpen: boolean;
    // shared
    isPatching: boolean;
    selectedOwner: Owner | undefined;
    percentageOwnershipAvailable: number | undefined;
  }>({
    isFullFormModalOpen: false,
    fullFormOwnerType: "authorized-individual",
    isUBOQuickAddModalOpen: false,
    isPatching: false,
    selectedOwner: undefined,
    percentageOwnershipAvailable: undefined,
  });

  const pageTimer = usePageTimer("entity_onboarding_auth_individuals_ubos");

  const queryClient = useQueryClient();

  const { account } = useGetTradingAccount();

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const { owners, refetch } = useGetOwners({
    accountId: account?.id || "",
    options: { enabled: !!account?.id },
  });

  const accountOpener = owners?.find(
    (details) => details.is_legal_account_opener,
  );

  const authorizedIndividuals =
    owners?.filter(
      ({ is_authorized_individual }) => is_authorized_individual,
    ) ?? [];
  const ultimateBeneficialOwners = owners?.filter(({ is_ubo }) => is_ubo) ?? [];

  const uboDropdownOptions =
    owners
      ?.filter(
        (
          { is_authorized_individual, family_name, given_name },
          _index,
          owners,
        ) =>
          is_authorized_individual &&
          // check and make sure they are not already UBOs
          !owners
            ?.filter(({ is_ubo }) => is_ubo)
            .find(
              (ubo) =>
                ubo.family_name === family_name &&
                ubo.given_name === given_name,
            ),
      )
      .map(({ owner_id, given_name, family_name, title_at_company }) => ({
        ownerID: owner_id,
        name: `${given_name} ${family_name}`,
        title: title_at_company ?? "",
      })) ?? [];

  const onBackClicked = async (evt: MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_ENTITY_ONBOARDING_ACCOUNT_OPENER, {
      isInEntityOnboarding: true,
    });
  };

  const onNextClicked = () => {
    if (ultimateBeneficialOwners.length <= 0) {
      toastError("You must add at least one Ultimate Beneficial Owner");
      return;
    }

    const uboPercentageTotal = getTotalPercentageOwnership(
      ultimateBeneficialOwners,
    );

    if (uboPercentageTotal > 100) {
      toastError(
        `The sum of each Ultimate Beneficial Owners' ownership percentage must be less than 100%`,
      );
      return;
    }
    const duration = pageTimer.stop();
    logAmplitudeEvent.pageCompleted(duration);

    // Invalidate the owners query to get the latest authorized individuals and UBOs
    queryClient.invalidateQueries({
      queryKey: queryKeys.documents.owners(account?.id ?? ""),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });

    logAmplitudeEvent.nextClicked();
    history.push(Path.ROUTE_ENTITY_ONBOARDING_DOCUMENTS, {
      isInEntityOnboarding: true,
    });
  };

  const onAddAuthorizedIndividualClicked = () => {
    setState({
      isFullFormModalOpen: true,
      isUBOQuickAddModalOpen: false,
      fullFormOwnerType: "authorized-individual",
      selectedOwner: undefined,
      percentageOwnershipAvailable: getAvailablePercentageOwnership(
        ultimateBeneficialOwners ?? [],
      ),
      isPatching: false,
    });
    logAmplitudeEvent.addOwnerClicked("authorized-individual");
  };

  const onAddUBOClicked = (authIndividualOwnerId?: string) => {
    // find authorized individual owner if user clicked on an existing authorized user in UBODropdownButton
    const owner = authIndividualOwnerId
      ? authorizedIndividuals?.find(
          (owner) => owner.owner_id === authIndividualOwnerId,
        )
      : undefined;

    setState({
      fullFormOwnerType: "ubo",
      isFullFormModalOpen: !owner,
      isUBOQuickAddModalOpen: !!owner,
      percentageOwnershipAvailable: getAvailablePercentageOwnership(
        ultimateBeneficialOwners ?? [],
      ),
      selectedOwner: owner,
      isPatching: false,
    });
    logAmplitudeEvent.addOwnerClicked("ubo");
  };

  const onClickEdit = (type: FormOwnerType, ownerId: string) => () => {
    const owner = owners?.find((owner) => owner.owner_id === ownerId) ?? null;

    if (!owner) {
      toastError("Error: Not found");
      return;
    }

    // Remove selected owner's percentage ownership from the total available so its editable
    const percentageOwnershipAvailable = getAvailablePercentageOwnership(
      ultimateBeneficialOwners?.filter((owner) => owner.owner_id !== ownerId) ??
        [],
    );

    const isOwnerAuthIndividualAndUBO =
      owner.is_ubo && owner.is_authorized_individual;
    const isUBOQuickEdit = isOwnerAuthIndividualAndUBO && type === "ubo";

    setState({
      isFullFormModalOpen: !isUBOQuickEdit,
      isUBOQuickAddModalOpen: isUBOQuickEdit,
      fullFormOwnerType: type,
      percentageOwnershipAvailable,
      selectedOwner: owner,
      isPatching: true,
    });
    logAmplitudeEvent.editOwnerClicked(type);
  };

  const authIndividualsColumns: TableColumn<Owner>[] = [
    {
      name: "Edit",
      key: "edit",
      width: 40,
      cellRenderer: ({ owner_id }) => (
        <Tooltip
          placement="top"
          content={
            accountOpener?.owner_id === owner_id
              ? "Account opener is automatically an authorized individual"
              : "Edit authorized individual"
          }
        >
          <Button
            variant="ghost"
            onClick={onClickEdit("authorized-individual", owner_id)}
            disabled={accountOpener?.owner_id === owner_id}
          >
            <Icon name="PencilSquare" />
          </Button>
        </Tooltip>
      ),
    },
    {
      name: "Name",
      key: "name",
      cellRenderer: (row) => `${row.given_name} ${row.family_name}`,
    },
    {
      name: "Title",
      key: "title_at_company",
      cellRenderer: (row) => `${row.title_at_company ?? ""}`,
    },
  ];

  const uboColumns: TableColumn<Owner>[] = [
    {
      name: "Edit",
      key: "edit",
      width: 40,
      cellRenderer: ({ owner_id }) => (
        <Button variant="ghost" onClick={onClickEdit("ubo", owner_id)}>
          <Icon name="PencilSquare" />
        </Button>
      ),
    },
    {
      name: "Name",
      key: "name",
      cellRenderer: (row) => `${row.given_name} ${row.family_name}`,
    },
    {
      name: "Title",
      key: "title_at_company",
      cellRenderer: (row) => `${row.title_at_company ?? ""}`,
    },
    {
      name: "Percentage Ownership",
      key: "percentage_ownership",
      width: 200,
      cellRenderer: (row) => `${row.percentage_ownership}%`,
    },
  ];

  const onCloseModal = () => {
    setState((prevState) => ({
      ...prevState,
      selectedOwner: undefined,
      isFullFormModalOpen: false,
      isUBOQuickAddModalOpen: false,
      isPatching: false,
    }));
    refetch();
  };

  // useEffect(() => {
  //   const percentageOwnershipAvailable = getAvailablePercentageOwnership(
  //     ultimateBeneficialOwners
  //   );

  //   setState((prevState) => ({
  //     ...prevState,
  //     percentageOwnershipAvailable,
  //   }));
  // }, [ultimateBeneficialOwners]);

  if (
    account &&
    !isLegalEntity &&
    !isLegalEntityLoading &&
    !history?.location?.state?.isInEntityOnboarding
  ) {
    history.replace(Path.ROUTE_NEW_ACCOUNT);
  }

  if (owners && owners.length >= 1 && !accountOpener) {
    history.replace(Path.ROUTE_ENTITY_ONBOARDING_ACCOUNT_OPENER);
  }

  const isLoading = !accountOpener;

  return (
    <>
      <OnboardingLayout
        title="Authorized Users & Ultimate Beneficial Owners"
        percentage={50}
        isLoading={isLoading}
        faq={AUTHORIZED_INDIVIDUAL_UBOS_FAQ}
      >
        <section>
          <h2 className="mb-3 font-medium text-lg">Authorized Users</h2>
          <Text className="text-gray-800">
            Anyone listed here will be able to trade, make changes to the
            account, and sign on behalf of your business. This usually includes
            executives or senior managers (like the CEO, CFO, or COO).
          </Text>

          <div className="my-5 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              className="h-10 items-center justify-center rounded border border-yellow-400 text-yellow-900"
              onClick={onAddAuthorizedIndividualClicked}
            >
              Add Authorized User
            </Button>
          </div>

          <Table
            columns={authIndividualsColumns}
            rows={authorizedIndividuals}
            emptyMessage="No Authorized Individuals"
          />
        </section>

        <section className="mt-12">
          <h2 className="mb-3 font-medium text-lg">
            Ultimate Beneficial Owners
          </h2>
          <Text className="text-gray-800">
            To ensure compliance with regulatory requirements, please identify
            all individuals who own 10% or more of your company's equity
            interests, either directly or indirectly.
            <br />
            <br />
            Important:
          </Text>
          <ul className="list-inside list-disc text-gray-800">
            <li className="pl-2">
              Do not list the names of legal entities, parent companies, or
              holding companies.
            </li>
            <li className="pl-2">
              If a parent or holding company owns 10% or more of your company,
              please provide the information of the individuals who ultimately
              own that parent or holding company.
            </li>
          </ul>

          <div className="my-5 flex justify-end">
            <UBODropdownButton
              options={uboDropdownOptions}
              onClick={onAddUBOClicked}
            />
          </div>

          <Table
            columns={uboColumns}
            rows={ultimateBeneficialOwners}
            emptyMessage="No Ultimate Beneficial Owners"
          />
        </section>

        <div className="flex w-full items-center justify-between gap-4 pt-9">
          <Button type="button" onClick={onBackClicked} variant="secondary">
            Back
          </Button>
          <Button type="button" onClick={onNextClicked}>
            Next
          </Button>
        </div>
      </OnboardingLayout>

      {state.isFullFormModalOpen && (
        <AuthorizedIndividualsUBOsForm
          isOpen={state.isFullFormModalOpen}
          title={`${state.isPatching ? "Edit" : "Add"} ${
            DISPLAY_TYPE[state.fullFormOwnerType]
          }`}
          isPatch={state.isPatching}
          owner={state.selectedOwner}
          type={state.fullFormOwnerType}
          percentageOwnershipAvailable={state.percentageOwnershipAvailable}
          country={country}
          onClose={onCloseModal}
        />
      )}

      {state.isUBOQuickAddModalOpen && (
        <UBOQuickAddForm
          owner={state.selectedOwner as Owner}
          onClose={onCloseModal}
          isOpen={state.isUBOQuickAddModalOpen}
          isPatch={state.isPatching}
          percentageOwnershipAvailable={state.percentageOwnershipAvailable}
        />
      )}
    </>
  );
}
